import { Application } from 'src/components/context/AppContextModels';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { EmbeddableProductType } from '../types/EmbeddingInterface';
import { FINSUITE_DOMAINS } from 'src/constants/AppConstants';
import { EMBED_TO_PARENT_MESSAGES } from 'src/constants/EmbedMessageConstants';

/**
 * Extracts the product type from the application's toolUI label.
 *
 * @param application - The application object to analyze
 * @returns The product type string or 'UNKNOWN' if not recognized
 */
export const getProductType = (application?: Application): string => {
  if (!application) return EmbeddableProductType.UNKNOWN;
  const toolLabel = application.toolUI?.[0]?.label || EmbeddableProductType.UNKNOWN;
  return toolLabel;
};

// Map for easily extendible product type mapping
const EMBEDDABLE_PRODUCT_MAP = new Map([
  ['Cloudscape (Polaris)', EmbeddableProductType.CLOUDSCAPE_POLARIS],
  ['Legacy Polaris', EmbeddableProductType.LEGACY_POLARIS],
  ['Prophecy', EmbeddableProductType.PROPHECY]
  // ['Skynet (Tableau)', EmbeddableProductType.SKYNET_TABLEAU], // Commented out - pending implementation
]);

/**
 * Determines if an application is eligible for embedding functionality.
 *
 * An application is eligible if it has a recognized product type that
 * supports embedding capabilities.
 *
 * @param application - The application to evaluate for embedding eligibility
 * @returns True if the application supports embedding, false otherwise
 */
export const isEligibleToEnableEmbedding = (application: Application): boolean => {
  const url = getEmbeddingURL(application);

  // to avoid self embedding
  const found = FINSUITE_DOMAINS.find((domain) => url?.includes(domain));
  if (found) return false;

  const productType = getProductType(application);
  const mappedType = EMBEDDABLE_PRODUCT_MAP.get(productType) || EmbeddableProductType.UNKNOWN;
  return mappedType !== EmbeddableProductType.UNKNOWN;
};

/**
 * Determines if embedding is both supported and enabled for an application.
 *
 * @param application - The application to check
 * @returns True if embedding is eligible and explicitly enabled
 */
export const isEmbeddingEnabled = (application: Application): boolean => {
  if (!application) return false;
  return isEligibleToEnableEmbedding(application) && application.embeddingEnabled === true;
};

/**
 * Retrieves the appropriate embedding URL based on the current environment.
 *
 * In non-production environments, returns the beta environment URL.
 * In production, returns the main application URL.
 *
 * @param application - The application containing URL configurations
 * @returns The embedding URL or undefined if not available
 */
export const getEmbeddingURL = (application: Application): string | undefined => {
  const isNonProd = ENVIRONMENT_VARIABLES.env.Stage !== 'prod';

  if (isNonProd) {
    // In non-production environments, use the non-production URL
    return application.nonProductionEnvironments?.find((nonProd) => nonProd.displayText?.toLowerCase() === 'beta')?.link || undefined;
  } else {
    // In production environment, use the production URL
    return application.applicationLink || undefined;
  }
};

export const EmbeddingURLErrorMessages = {
  URL_NOT_CONFIGURED: `URL is not configured`,
  URL_CONFIGURE_UNKNOWN: `Unknown error while configuring URL`
};

// ─── Cross-Frame Messaging Utilities ───────────────────────────────────────────
// These utilities support the postMessage protocol between FinSuite (parent)
// and embedded applications (child iframe). See EmbedMessageConstants.ts for
// the full protocol documentation.

/**
 * Sends a postMessage to the currently embedded iframe.
 *
 * @param type - The message type constant (from PARENT_TO_EMBED_MESSAGES)
 * @param payload - Optional payload data to send with the message
 * @returns true if the message was sent, false if no iframe was found
 */
export const postMessageToEmbeddedIframe = (type: string, payload?: Record<string, unknown>): boolean => {
  const iframe = document.querySelector('iframe') as HTMLIFrameElement;
  if (!iframe?.contentWindow) return false;
  iframe.contentWindow.postMessage({ type, payload }, '*');
  return true;
};

/**
 * Checks if a MessageEvent is the embed ready handshake signal from an embedded app.
 *
 * @param event - The MessageEvent to check
 * @returns true if the event is a FINSUITE_EMBED_READY message
 */
export const isEmbedReadyMessage = (event: MessageEvent): boolean => {
  return event.data?.type === EMBED_TO_PARENT_MESSAGES.EMBED_READY;
};
